import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";

/**
 * EntitlementGate - Guards premium features based on user tier
 * 
 * Usage:
 * <EntitlementGate feature="outreach" user={user}>
 *   <PremiumFeature />
 * </EntitlementGate>
 */

// Feature to tier mapping
const FEATURE_TIERS = {
  proofs: ['pro', 'growth', 'teams'],
  outreach: ['growth', 'teams'],
  video: ['growth', 'teams'],
  contract_builder: ['pro', 'growth', 'teams']
};

const FEATURE_NAMES = {
  proofs: 'Proof Builder',
  outreach: 'Cold Outreach Pro',
  video: 'Video Testimonials',
  contract_builder: 'Contract Builder'
};

const UPGRADE_TIERS = {
  proofs: 'Pro',
  outreach: 'Growth',
  video: 'Growth',
  contract_builder: 'Pro'
};

export function hasFeatureAccess(user, feature) {
  if (!user) return false;
  const tier = user.tier || 'free';
  const allowedTiers = FEATURE_TIERS[feature] || [];
  return allowedTiers.includes(tier);
}

export default function EntitlementGate({ feature, user, children, showUpgrade = true }) {
  const hasAccess = hasFeatureAccess(user, feature);
  
  if (hasAccess) {
    return <>{children}</>;
  }
  
  if (!showUpgrade) {
    return null;
  }
  
  const featureName = FEATURE_NAMES[feature] || feature;
  const requiredTier = UPGRADE_TIERS[feature] || 'Pro';
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-testid={`gate-${feature}`}>
      <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center mb-6">
        <Lock className="w-10 h-10 text-slate-400" />
      </div>
      
      <h2 className="text-2xl font-bold text-indigo-500 mb-2">
        {featureName} is a Premium Feature
      </h2>
      
      <p className="text-indigo-400 max-w-md mb-6">
        Upgrade to {requiredTier} to unlock {featureName} and other powerful tools for growing your freelance business.
      </p>
      
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-all"
      >
        <Sparkles className="w-5 h-5" />
        Upgrade to {requiredTier}
      </Link>
      
      <p className="text-sm text-slate-400 mt-4">
        Current plan: <span className="text-indigo-400 capitalize">{user?.tier || 'Free'}</span>
      </p>
    </div>
  );
}
